import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { redirect, useLocation, useNavigate } from 'react-router';
import { ResponseError, type LanguageCode } from '@linktivity/ars-neo';
import { message, Tag } from '@linktivity/link-ui';
import VerticalProductCard from '@link/share/business/VerticalProductCard';
import FAQItem from '@link/share/components/FAQItem';
import { Images } from '@link/share/components/Images';
import NotificationList from '@link/share/components/NotificationList';
import ReactMarkdown from '@link/share/components/ReactMarkdown';
import {
  buildLocalizedPath,
  buildPathWithRedirect,
  getActivityCategoryTags,
  resolveProductPriceInfo
} from '@link/share/utils';
import { getAccessToken } from '../../auth/tokens';
import { CAPABILITIES } from '../../capabilities';
import { DEFAULT_LOCALE, resolveAppLocale, type Locale } from '../../i18n';
import { metaT } from '../../seo';
import type { Route } from './+types/Product';
import { describeApiError } from './booking/api';
import { BookingStoreProvider } from './booking/context';
import { BookingOptions } from './booking/options/BookingOptions';
import { BookingPayment } from './booking/payment/BookingPayment';
import { PlanDetailsForCrawlers } from './booking/plans/PlanDetailsForCrawlers';
import { ProductPlans } from './booking/plans/ProductPlans';
import { BookingStore } from './booking/stores/booking';
import { loadProduct } from './product.server';
import { buildProductMeta, supportedLocales } from './productSeo';
import styles from './product.module.css';

// Portals, which the server cannot render; loaded once the page has hydrated.
const BookingDialogs = lazy(() =>
  import('./booking/dialogs/BookingDialogs').then(m => ({
    default: m.BookingDialogs
  }))
);

export async function loader({ params, request }: Route.LoaderArgs) {
  const locale = resolveAppLocale(params.lang);
  // Uppercase enum, same as the home loader; see the note there.
  const data = await loadProduct(
    locale.toUpperCase() as Parameters<typeof loadProduct>[0],
    params.productId
  );
  if (!data.product.id) {
    throw redirectToSupportedLocale(data.product, locale, request, params);
  }
  return { ...data, locale, url: request.url };
}

/**
 * Send a visitor on an untranslated locale to a version of the product that
 * exists.
 *
 * The SPA rendered the empty page and opened a dialog asking which supported
 * language to switch to, pre-selecting the current one if supported and
 * otherwise the first. Server-side that page has no content, so answering it
 * with 200 would hand crawlers a soft 404, and a 404 would strand visitors who
 * reached it through the language menu. Redirecting lands both on the version
 * the dialog would have defaulted to — preferring the site default, which is
 * also what `x-default` points at.
 *
 * 302, not 301: a translation can be added later, and the redirect must stop
 * when it is.
 */
function redirectToSupportedLocale(
  product: Parameters<typeof supportedLocales>[0],
  locale: Locale,
  request: Request,
  params: Route.LoaderArgs['params']
) {
  const supported = supportedLocales(product).filter(code => code !== locale);
  const target = supported.includes(DEFAULT_LOCALE)
    ? DEFAULT_LOCALE
    : supported[0];
  // Nothing to fall back to: the API reported no other language.
  if (!target) {
    return new Response('Product not found', { status: 404 });
  }
  const { search } = new URL(request.url);
  // Re-encoded for the same reason as homeRedirect: params arrive decoded, and
  // an unencoded value in `Location` can point off-site.
  return redirect(
    `/${target}/product/${encodeURIComponent(params.productId)}${search}`,
    302
  );
}

/**
 * Page metadata and JSON-LD, rendered into the server HTML. Replaces the SPA's
 * `useSEO()` + `useProductStructuredData()`; see productSeo.ts.
 */
export function meta({ loaderData, matches }: Route.MetaArgs) {
  const t = metaT(matches[0].loaderData);
  // The loader threw — unknown product or upstream failure. Same reasoning as
  // the home page: no canonical or hreflang for an error page.
  if (!loaderData) {
    return [
      { title: t('triplabo.home.title') },
      { name: 'robots', content: 'noindex, nofollow' }
    ];
  }
  return buildProductMeta(loaderData, t);
}

/**
 * Keyed by product and language: another product, or this one in another
 * language, is a fresh page with fresh booking state — what the SPA's hook
 * reset by hand.
 */
export default function Product({ loaderData }: Route.ComponentProps) {
  return (
    <ProductPage
      key={`${loaderData.locale}:${loaderData.product.id}`}
      data={loaderData}
    />
  );
}

/**
 * The SSR counterpart to `src/views/Product`.
 *
 * The product's content — images, title, notices, highlight, description,
 * cancellation policy, FAQ, recommendations — renders from loader data, so all
 * of it is in the HTML crawlers receive. Layout and class names follow the SPA
 * view so the two render the same.
 *
 * The booking area needs live availability, which is different every time you
 * look and has no place in a cached page. What it can show from the product
 * renders on the server too — plan names, pictures and starting prices, every
 * plan's details (hidden), the options, the payment panel — with skeletons
 * where the calendar goes. After hydration the browser fetches the calendar
 * (see BookingStore) and loads the dialogs.
 */
function ProductPage({ data }: { data: Route.ComponentProps['loaderData'] }) {
  const { product, recommendations, notifications, site, currency, locale } =
    data;
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [hydrated, setHydrated] = useState(false);
  useEffect(() => setHydrated(true), []);
  const activity = product.productContent?.activity;
  const title = activity?.title ?? '';
  const images = activity?.imageUrls ?? [];
  // Activity categories, not the curated `product.tags` — matches
  // `deriveProductDisplayInfo` in the SPA.
  const tags = getActivityCategoryTags(activity?.categories, t);
  const highlight = product.productContent?.highlight ?? '';
  const description = activity?.description ?? '';
  const cancelPolicy = activity?.cancelPolicy;
  const faqs = activity?.faqs ?? [];
  const showOtherCurrencies =
    site.siteLayout?.headerConfig?.showOtherCurrencies ?? false;

  const showError = useCallback(
    async (error: unknown) => {
      // The token was refused even after a refresh, and has been cleared:
      // the visitor signs in again and comes back here.
      if (error instanceof ResponseError && error.response.status === 401) {
        navigate(loginPath(locale, location.pathname + location.search));
        return;
      }
      message.error(
        await describeApiError(error, t, key => i18n.exists(key)),
        10
      );
    },
    [navigate, locale, location.pathname, location.search, t, i18n]
  );

  // One store per page: ProductPage is keyed by product and language, so a
  // new product or language is a new store.
  const [store] = useState(
    () =>
      new BookingStore({
        product,
        locale,
        languageCode: locale.toUpperCase() as LanguageCode,
        currency,
        showOtherCurrencies,
        enableColumnPlanAutoSwitch: CAPABILITIES.columnPlanAutoSwitch
      })
  );

  // Browser only: effects never run on the server. Each call does its work
  // once per store, so a re-run — StrictMode's second mount, a new
  // `showError` — sends nothing again.
  useEffect(() => {
    store.trackView();
    store.loadCalendar().catch(showError);
    store.loadOptionCalendars().catch(showError);
  }, [store, showError]);

  useEffect(() => store.startClock(), [store]);

  const loadMoreCalendar = useCallback(() => {
    store.loadMoreCalendar().catch(showError);
  }, [store, showError]);

  const goToConfirm = useCallback(async () => {
    const sessionId = await store.book().catch(error => {
      showError(error);
      return undefined;
    });
    if (sessionId) {
      navigate(
        `/${locale}/book/confirm?sessionId=${encodeURIComponent(sessionId)}`
      );
    }
  }, [store, showError, navigate, locale]);

  // Booking needs a signed-in visitor. The SPA asked in a login dialog; until
  // that moves here, the visitor goes to the login page and returns.
  const handleSubmit = useCallback(() => {
    if (!getAccessToken()) {
      navigate(loginPath(locale, location.pathname + location.search));
      return;
    }
    if (store.hasConfirmationPopup) {
      store.setOrderConfirmVisible(true);
    } else {
      goToConfirm();
    }
  }, [
    store,
    goToConfirm,
    navigate,
    locale,
    location.pathname,
    location.search
  ]);

  return (
    <BookingStoreProvider value={store}>
      <div className={styles.product}>
        <div className={styles.productImageContainer}>
          <Images images={images} alt={title} className={styles.productImage} />
        </div>
        <div className={styles.inner}>
          <div className={styles.left}>
            <div className={styles.header}>
              <h1 className={styles.title}>{title}</h1>
              {tags.length > 0 && (
                <div className={styles.tags}>
                  {tags.map(tag => (
                    <Tag key={tag} text={tag} type="info" />
                  ))}
                </div>
              )}
            </div>
            <NotificationList
              notifications={notifications}
              itemClassName={styles.notification}
            />
            {!!highlight && (
              <ReactMarkdown className={styles.highlight} source={highlight} />
            )}
            {!!description && (
              <ReactMarkdown
                className={styles.description}
                source={description}
              />
            )}
            <ProductPlans onLoadMore={loadMoreCalendar} />
            <PlanDetailsForCrawlers />
            {store.optionProducts.length > 0 && (
              <div className={styles.options}>
                <h4 className={styles.optionsTitle}>
                  {t('triplabo.product.panels.options')}
                  {store.needsOptionCalendars && (
                    <Tag
                      className={styles.optionsRequiredTag}
                      text={t('share.payment.optionRequiredTag')}
                      type="danger"
                    />
                  )}
                </h4>
                <div className={styles.optionsContent}>
                  <BookingOptions />
                </div>
              </div>
            )}
            <div className={styles.cancelPolicy}>
              <p className={styles.cancelPolicyTitle}>
                {cancelPolicy?.title ||
                  t('triplabo.product.cancelPolicy.title')}
              </p>
              <div className={styles.cancelPolicyContent}>
                <ReactMarkdown source={cancelPolicy?.description ?? ''} />
              </div>
            </div>
            {faqs.length > 0 && (
              <div className={styles.faqSection}>
                <h2 className={styles.faqTitle}>{t('views.faq.title')}</h2>
                <div className={styles.faqItem}>
                  <FAQItem items={faqs} />
                </div>
              </div>
            )}
            {recommendations.length > 0 && (
              <div className={styles.recommendationsSection}>
                <h4 className={styles.recommendationsTitle}>
                  {t('triplabo.product.recommendations.title')}
                </h4>
                <div className={styles.recommendationsContent}>
                  <div className={styles.recommendationsList}>
                    {recommendations.map(item => {
                      const itemActivity = item.productContent?.activity;
                      // Same card mapping as `useRecommendations` in the SPA:
                      // category tags and the activity's own amount.
                      return (
                        <VerticalProductCard
                          key={item.id}
                          image={itemActivity?.imageUrls?.[0] ?? ''}
                          title={itemActivity?.title ?? ''}
                          tags={getActivityCategoryTags(
                            itemActivity?.categories,
                            t
                          )}
                          priceInfo={resolveProductPriceInfo(
                            item,
                            itemActivity?.amount ?? 0,
                            currency,
                            showOtherCurrencies
                          )}
                          to={
                            item.id
                              ? buildLocalizedPath(`product/${item.id}`, locale)
                              : ''
                          }
                          className={styles.recommendationCard}
                        />
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className={styles.right}>
            <BookingPayment onSubmit={handleSubmit} />
          </div>
        </div>
        {hydrated && (
          <Suspense fallback={null}>
            <BookingDialogs onAgree={goToConfirm} />
          </Suspense>
        )}
      </div>
    </BookingStoreProvider>
  );
}

/** The login page, returning to `returnPath` once signed in. */
const loginPath = (locale: Locale, returnPath: string) =>
  buildPathWithRedirect(buildLocalizedPath('login', locale), returnPath);
